<template>
  <div
    v-if="show"
    ref="warningModal"
    class="price-update-warning"
    :class="{'highlight-focus': isHighlighted}"
    tabindex="-1"
  >
    <!-- Triangular pointer at the top -->
    <div class="triangle-pointer">
      <triangle-pointer-icon />
    </div>

    <!-- Warning header -->
    <div class="warning-message">
      <feather-icon
        icon="AlertTriangleIcon"
        size="24"
        class="warning-icon"
      />
      <div class="message-content">
        <p class="mb-0">
          <strong>Some SKUs with updated prices exist in orders from retailers</strong><br>
          Would you like to update prices in these orders?
        </p>
      </div>
      <div class="button-group">
        <b-button
          variant="outline-primary"
          class="mr-2"
          :class="{ 'active': updateOrderPrices === false }"
          @click="handleButtonClick(false)"
        >
          No
        </b-button>
        <b-button
          variant="outline-primary"
          :class="{ 'active': updateOrderPrices === true }"
          @click="handleButtonClick(true)"
        >
          Yes, update prices
        </b-button>
      </div>
    </div>

    <!-- Loading spinner when orders are being fetched -->
    <div
      v-if="updateOrderPrices && isLoading"
      class="loading-spinner-container"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
      <span class="ml-2">Loading orders...</span>
    </div>

    <!-- No orders message -->
    <div
      v-if="updateOrderPrices && !isLoading && (!orders || orders.length === 0)"
      class="no-orders-message"
    >
      <p>No orders found with the updated product prices.</p>
    </div>

    <!-- Orders list section - only shown when "Yes" is selected -->
    <div
      v-if="updateOrderPrices && orders && orders.length > 0"
      class="orders-list-section"
    >
      <hr>
      <h6>
        Updating prices in orders
      </h6>

      <!-- Search input -->
      <div class="position-relative search-input">
        <b-form-input
          v-model="orderSearchText"
          placeholder="Search by order id, retailer name, etc."
          @input="filterOrders"
        />
        <feather-icon
          icon="SearchIcon"
          size="18"
          class="search-icon"
        />
      </div>

      <!-- Orders table header -->
      <div class="orders-table-header">
        <div class="d-flex align-items-center">
          <b-form-checkbox
            v-model="selectAllOrders"
            class="mr-2"
            :indeterminate="isIndeterminate"
            @change="toggleSelectAll"
          />
          <strong class="order-id-column order-column-label">ORDER ID</strong>
          <strong class="retailer-name-column">RETAILER NAME</strong>
        </div>
      </div>

      <!-- Orders rows -->
      <div class="orders-table-body">
        <div
          v-for="(order, index) in filteredOrders"
          :key="index"
          class="order-row"
        >
          <b-form-checkbox
            v-model="selectedOrders"
            :value="order"
            class="mr-2"
          />
          <div class="order-id-column">
            {{ order.orderSeqId || `Order-${index+1}` }}
          </div>
          <div class="retailer-name-column">
            {{ order.retailerName }}
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="orders-actions mt-3">
        <b-button
          variant="light"
          class="modal-btn close-modal-btn"
          @click="cancelOrderUpdate"
        >
          Cancel
        </b-button>
        <b-button
          variant="light"
          class="save-modal-btn"
          :disabled="selectedOrders.length === 0"
          @click="updateSelectedOrders"
        >
          {{ 'Update selected orders' + (selectedOrders.length ? ` (${selectedOrders.length})` : '') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
 BButton, BFormCheckbox, BFormInput, BSpinner,
} from 'bootstrap-vue'
import { GET_PRODUCT_ORDERS, UPDATE_ORDER_PRODUCT_PRICES } from '@/store/modules/productEdit.module'
import TrianglePointerIcon from '@/@core/assets/svg-components/TrianglePointerIcon.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import store from '@/store'

export default {
  name: 'PriceUpdateWarning',
  components: {
    BFormCheckbox,
    BButton,
    BFormInput,
    BSpinner,
    TrianglePointerIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    wholesalePrice: {
      type: [Number, String],
      required: false,
      default: null,
    },
    retailPrice: {
      type: [Number, String],
      required: false,
      default: null,
    },
    originalWholesalePrice: {
      type: [Number, String],
      required: false,
      default: null,
    },
    originalRetailPrice: {
      type: [Number, String],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      updateOrderPrices: null,
      orders: [],
      selectedOrders: [],
      selectAllOrders: false,
      orderSearchText: '',
      filteredOrders: [],
      isLoading: false,
      isHighlighted: false,
    }
  },
  computed: {
    isIndeterminate() {
      return this.selectedOrders.length > 0 && this.selectedOrders.length < this.filteredOrders.length
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // If the modal is being shown, highlight it
        this.$nextTick(() => {
          this.focusAndHighlight()
        })
      } else {
        // Reset highlight when hidden
        this.isHighlighted = false
      }
      this.handleDialogVisibility()
    },
    updateOrderPrices: 'handleDialogVisibility',
    selectedOrders: 'updateSelectAllState',
  },
  methods: {
    getTrackData() {
      return {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        newPrices: {
          wholesalePrice: this.wholesalePrice,
          retailPrice: this.retailPrice,
        },
        oldPrices: {
          wholesalePrice: this.originalWholesalePrice,
          retailPrice: this.originalRetailPrice,
        },
      }
    },
    handleButtonClick(val) {
      this.updateOrderPrices = val

      const selectedOption = val ? 'Yes, update prices' : 'No'
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SELECTS_AN_OPTION_TO_UPDATE, {
        ...this.getTrackData(),
        selectedOption,
      })

      if (val) {
        this.fetchOrdersData()
      } else {
        // Close the modal when "No" is clicked
        this.$emit('cancel')
        // Emit event to parent that the price update flow is closed
        this.$root.$emit('price-update-closed')
      }
    },
    async handleDialogVisibility() {
      if (this.show && this.updateOrderPrices) {
        await this.fetchOrdersData()
      }
    },

    async fetchOrdersData() {
      if (!this.productId) return

      this.isLoading = true

      try {
        const orders = (await this.$store.dispatch(GET_PRODUCT_ORDERS, {
          productId: this.productId,
        })).filter(({ status }) => !!status)

        this.orders = orders
        this.filteredOrders = orders
        this.selectedOrders = []
        this.selectAllOrders = false
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_FETCHED_AND_DISPLAYED, {
          ...this.getTrackData(),
          orders,
        })
      } catch (error) {
        console.error('Failed to fetch orders:', error)
        this.orders = []
        this.filteredOrders = []
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ERROR_FETCHING_ORDERS, {
          ...this.getTrackData(),
          error,
        })
      } finally {
        this.isLoading = false
      }
    },
    filterOrders() {
      const search = this.orderSearchText.toLowerCase()
      this.filteredOrders = search
        ? this.orders.filter(({ orderSeqId, retailerName }) => orderSeqId?.toLowerCase().includes(search)
          || retailerName?.toLowerCase().includes(search))
        : this.orders
      this.updateSelectAllState()
    },
    toggleSelectAll(checked) {
      this.selectedOrders = checked ? [...this.filteredOrders] : []
    },
    updateSelectAllState() {
      // Update the selectAllOrders state based on selectedOrders
      this.selectAllOrders = this.filteredOrders.length > 0
        && this.selectedOrders.length === this.filteredOrders.length

      const trackName = this.selectAllOrders ? 'BRAND_SELECTS_ALL_ORDERS_FOR_UPDATE' : 'BRAND_SELECTS_ORDER_FOR_UPDATE'
      if (this.selectedOrders.length) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT[trackName], {
          ...this.getTrackData(),
          selectedOrders: this.selectedOrders,
        })
      }
    },
    cancelOrderUpdate() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_CANCEL_BUTTON, {
        ...this.getTrackData(),
        selectedOrders: this.selectedOrders,
      })
      this.updateOrderPrices = false
      this.$emit('cancel')
      // Emit event to parent that the price update flow is closed
      this.$root.$emit('price-update-closed')
    },
    async updateSelectedOrders() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_UPDATE_SELECTED_ORDERS_BUTTON, {
        ...this.getTrackData(),
        selectedOrders: this.selectedOrders,
      })
      const payload = this.buildPriceUpdatePayload()
      if (!payload) {
        this.$root.$emit('price-update-closed')
        return
      }

      try {
        const response = await this.$store.dispatch(UPDATE_ORDER_PRODUCT_PRICES, payload)

        // Determine how many orders were updated
        const updatedCount = payload.orderIds.length || 0

        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_UPDATE_SUCCESS, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          payload,
          response,
        })

        // Close the warning dialog first
        this.$emit('update-orders', this.selectedOrders)
        this.$emit('close')

        // Emit the count of updated orders for the success toast with a slight delay
        // to ensure the modal closes before showing the toast
        setTimeout(() => {
          this.$root.$emit('orders-price-updated', updatedCount)
        }, 100)

        // After a delay, emit that the price update flow is closed
        setTimeout(() => {
          this.$root.$emit('price-update-closed')
        }, 2500) // Give enough time for toast to be shown and hidden
      } catch (err) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_UPDATE_FAILED, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          payload,
          error: err,
        })
        console.error('Price update failed:', err)
        // Still close the flow on error
        this.$root.$emit('price-update-closed')
      }
    },
    buildPriceUpdatePayload() {
      const productId = this.productId
      const items = this.items || []
      const wholesale = this.wholesalePrice
      const retail = this.retailPrice

      const hasItems = items.length > 0
      const hasPrice = wholesale != null || retail != null

      if (!productId || !hasItems || !hasPrice) return null

      const toNumber = value => Number(value)

      const mapPriceFields = () => ({
        ...(wholesale != null && { price: toNumber(wholesale) }),
        ...(retail != null && { retailPrice: toNumber(retail) }),
      })

      return ({
        orderIds: this.selectedOrders.map(order => order._id),
        products: [
          {
            productId,
            ...mapPriceFields(),
            items: items.map(({ sku }) => ({
              sku,
              ...mapPriceFields(),
            })),
          },
        ],
      })
    },
    focusAndHighlight() {
      // Focus the modal
      if (this.$refs.warningModal) {
        this.$refs.warningModal.focus()

        // Apply highlight effect
        this.isHighlighted = true

        // Scroll into view if needed
        this.$refs.warningModal.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })

        // Remove highlight after a short delay
        setTimeout(() => {
          this.isHighlighted = false
        }, 1500)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.price-update-warning {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #E0E0E0;
  background: #F7F7F7;
  margin-bottom: 1rem;
  position: relative;
  margin-top: 3px;
  transition: all 0.3s ease;
  outline: none;

  &.highlight-focus {
    box-shadow: 0 0 0 3px rgba(21, 161, 204, 0.5);
    border-color: #15A1CC;
    animation: pulse 1.5s 1;
  }

  .triangle-pointer {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 33px;
    height: 16px;
    margin: 0 auto;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .warning-message {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;

    .warning-icon {
      color: #292929;
      stroke-width: 2;
      margin-top: 2px;
      flex-shrink: 0;
    }

    .message-content {
      flex: 1;

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;

        strong {
          font-weight: 600;
          color: #1a1a1a;
        }
      }
    }

    .button-group {
      margin-left: auto;
      flex-shrink: 0;

      .active {
        background-color: #15A1CC !important;
        border-color: #15A1CC !important;
        color: #fff !important;
      }
    }
  }

  hr {
    width: 100%;
    margin: 0;
    border-top: 1px solid #E0E0E0;
  }

  .orders-list-section {
    width: 100%;
    .search-input {
      margin-bottom: 16px;
    }

    h6 {
      font-weight: 600;
      margin-top: 1rem;
    }

    .position-relative {
      .search-icon {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .orders-table-header {
      padding: 0.5rem 0;
      border-bottom: 1px solid #E0E0E0;
      font-size: 12px;

      strong {
        font-weight: 700;
      }
    }

    .order-row {
      padding: 0.75rem 0;
      border-bottom: 1px solid #F0F0F0;
      display: flex;
      align-items: center;
    }

    .order-id-column {
      width: 60%;
      padding-right: 1rem;
    }

    .retailer-name-column {
      width: 40%;
    }

    .orders-actions {
      display: flex;
      justify-content: flex-end;
      gap: 7px;

      .close-modal-btn {
        background: #FFF;
      }
    }
  }

  .loading-spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .no-orders-message {
    padding: 1rem;
  }

  ::v-deep .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before,
  ::v-deep .b-form-checkbox.custom-control .custom-control-input:checked ~ .custom-control-label::before,
  ::v-deep .form-check-input:checked {
    background-color: #15A1CC !important;
    border-color: #15A1CC !important;
    box-shadow: none !important;
  }

  ::v-deep .custom-checkbox .custom-control-input:indeterminate ~ .custom-control-label::before,
  ::v-deep .b-form-checkbox.custom-control .custom-control-input:indeterminate ~ .custom-control-label::before,
  ::v-deep .form-check-input:indeterminate {
    background-color: #15A1CC !important;
    border-color: #15A1CC !important;
    box-shadow: none !important;
  }

  ::v-deep .custom-checkbox .custom-control-input:focus ~ .custom-control-label::before,
  ::v-deep .b-form-checkbox.custom-control .custom-control-input:focus ~ .custom-control-label::before,
  ::v-deep .form-check-input:focus {
    box-shadow: 0 0 0 0.2rem rgba(21, 161, 204, 0.25) !important;
  }

  // Additional targeting for Bootstrap checkboxes
  ::v-deep .form-check-input {
    &:checked {
      background-color: #15A1CC !important;
      border-color: #15A1CC !important;
    }

    &:indeterminate {
      background-color: #15A1CC !important;
      border-color: #15A1CC !important;
    }

    &:focus {
      border-color: #15A1CC !important;
      box-shadow: 0 0 0 0.25rem rgba(21, 161, 204, 0.25) !important;
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(21, 161, 204, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(21, 161, 204, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(21, 161, 204, 0);
  }
}
</style>
